<template>
  <div class="onboarding-signup-step-photo">
    <transition appear :enter-active-class="transition.classes.enter" :leave-active-class="transition.classes.leave">
      <QPage padding>
        <h2 class="step-title-optional">
          {{ $filters.whitespacedPunctuation($t("ETAB.PAGES.SIGNUP.STEPS.INFO.PHOTO.@TITLE")) }}
          <ExtraTag :label="$t('ETAB.BUTTONS.@OPTIONAL')" />
        </h2>

        <section>
          <img v-if="isMobile" :src="etabImg" :draggable="false" @click="openGallery" />

          <ExtraQFileBtn v-else :ref="FILE_INPUT_REFERENCE" style="width: 250px" @update:model-value="openFileSystem" @rejected="onRejected">
            <img :src="etabImg" :draggable="false" />
          </ExtraQFileBtn>
        </section>

        <ExtraTipbox>
          <span>{{ $t("ETAB.PAGES.SIGNUP.STEPS.INFO.PHOTO.@NOTICE") }}</span>
        </ExtraTipbox>
      </QPage>
    </transition>

    <QFooter>
      <div class="row justify-center align-middle">
        <QBtn color="white" text-color="primary" class="btn-navigation-half" :label="$t('ETAB.BUTTONS.@IGNORE')" no-caps @click="goToSuccess" />
        <QBtn v-if="isMobile" color="secondary" class="btn-navigation-half" :label="$t('ETAB.BUTTONS.@ADD_PHOTO')" no-caps @click="openGallery" />
        <ExtraQFileBtn
          v-else
          :ref="FILE_INPUT_REFERENCE"
          style="width: calc(100% / 2 - 10px)"
          @update:model-value="openFileSystem"
          @rejected="onRejected"
        >
          <QBtn
            color="secondary"
            class="btn-navigation-half no-pointer-events onboarding-signup-step-photo-btn"
            :label="$t('ETAB.BUTTONS.@ADD_PHOTO')"
            no-caps
          />
        </ExtraQFileBtn>
      </div>
    </QFooter>
  </div>
</template>

<script setup>
  import etabImg from "@etab/assets/img/photo_etablissement.svg";
  import { Camera } from "@core/plugins/mobile";
  import { ACTION_SIGNUP_UPDATE } from "@etab/stores/types";
  import { useSignupStore } from "@etab/stores/signup";
  import { ETAB_ROUTES } from "@etab/config";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { TrackingManager } from "@core/managers/tracking.manager";
  import { FIREBASE_EVENTS } from "@core/constants";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { useRouter } from "vue-router";
  import { useSignup } from "@etab/composables";
  import { useTransition } from "@core/composables";
  import { ISignup } from "@core/models";
  import { ref } from "vue";

  const { transition } = useTransition();
  const { $_nextStep } = useSignup();
  const router = useRouter();
  const signupStore = useSignupStore();

  const FILE_INPUT_REFERENCE = ref("FILE_INPUT");

  const openGallery = async function () {
    try {
      // we reduce quality for performance because the picture will be small
      const cameraOptions = { quality: 50 };

      const image = await Camera.getPhoto(cameraOptions);
      const sanitizedImage = Camera.sanitizeBase64(image.base64String);
      const base64img = `data:image/jpeg;base64,${sanitizedImage}`;

      signupStore[ACTION_SIGNUP_UPDATE]({ [ISignup.img]: base64img });
      TrackingManager.addEvent({
        event: FIREBASE_EVENTS.FUNNEL_SECOND_STEP,
        step: "step4_photo",
      });
      $_nextStep();
    } catch (error) {
      // do not show error here because it could be from any reason
      // for instance just closing the modal on desktop will create an error "cancelled by user"
      throw error;
    }
  };

  const openFileSystem = async function (file) {
    try {
      LoadingManager.show();
      const base64String = await Camera.getBase64FromFile(file);
      const sanitizedImage = Camera.sanitizeBase64(base64String);
      signupStore[ACTION_SIGNUP_UPDATE]({ [ISignup.img]: sanitizedImage });
      TrackingManager.addEvent({
        event: FIREBASE_EVENTS.FUNNEL_SECOND_STEP,
        step: "step4_photo",
      });
      $_nextStep();
    } catch (error) {
      NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
    } finally {
      LoadingManager.hide();
    }
  };

  const onRejected = function (rejectedEntries) {
    NotificationsManager.error({ message: "ERRORS.@TRYING_TO_UPLOAD_WRONG_TYPE" });
  };

  const goToSuccess = function () {
    TrackingManager.addEvent({
      event: FIREBASE_EVENTS.FUNNEL_SECOND_STEP,
      step: "step4_photo_ignore",
    });
    router.push({ name: ETAB_ROUTES.signup.steps.success.name, query: { update: true } }).catch((suppressedError) => {});
  };
</script>

<style lang="scss">
  .onboarding-signup-step-photo {
    .q-page {
      > section {
        display: flex;
        align-items: center;
        flex-direction: column;
        img {
          width: 250px;
          max-width: 250px;
          margin: 25px 0;
        }
      }
    }

    .extra-tipbox {
      margin-top: 25px;
    }

    .onboarding-signup-step-photo-btn {
      position: relative;
      font-size: 16px;
      padding: 4px;
      top: 1px;
      height: 54px;
      width: 100%;
    }
  }
</style>
